import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import * as _ from 'lodash-es';
import { Fir163jTaggingService } from '../fir-163j-tagging.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  normalizeFilterParams
} from '../../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-fir-163j-sync-to-8990',
    templateUrl: './fir-163j-sync-to-8990.component.html',
    styleUrls: ['./fir-163j-sync-to-8990.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Fir163jSyncTo8990Component implements OnInit, OnDestroy {
  title: string = "Confirm";
  loading: boolean = false;
  processing: boolean = false;

  private unsubscribe$ = new Subject<void>();
  private subscriptions = new Subscription();

  generalConfig: any = {};
  logged_in_user = '';
  baseGlobalParams: any = {};
  filterParams: any = {};
  gridData: any = {};
  syncSaveDtls: any[] = [];

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  constructor(
    private fir163jTaggingService: Fir163jTaggingService,
    private gtwSnackbarService: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.processGridData();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.processGridData();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.processGridData();
      })
    );
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.generalConfig = generalConfigFromGlobal(this.gtwGlobal);
    this.baseGlobalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    const sel = activeScreen?.data?.selected;
    const g = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = sel != null ? sel : g != null ? g : this.gridData;
    const sso = this.gtwApp?.USER_SETTINGS?.user?.sso_id;
    this.logged_in_user = sso != null && sso !== '' ? String(sso) : this.logged_in_user;
  }

  processGridData() {
    if (!this.gridData) return;

    this.syncSaveDtls = [];
    const tempGridData = _.clone(this.gridData);

    if (tempGridData) {
      for (const key in tempGridData) {
        const item = tempGridData[key];
        if (!item) continue;
        const row = item.data || item;
        if (!row || typeof row !== 'object') continue;
        this.syncSaveDtls.push({
          object_id: row.object_id,
          system_acct: row.SYS_ACCT,
          system_acct_desc: row.SYS_ACCT_DESC,
          system_acct_key: row.SYS_ACCT_KEY
        });
      }
    }
  }

  confirmSync(): void {
    this.processing = true;
    this.loading = true;

    const returnClobSettingsObj: any = {};
    const ecSaveDtls: any[] = [];

    const sysacct = this.syncSaveDtls.map(item => item.system_acct);
    const sysacctString = sysacct.join(',');

    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    returnClobSettingsObj['system_acct'] = sysacctString;

    this.sendFormSyncDetails(returnClobSettingsObj, ecSaveDtls, "163j will be synced to Form 8990");
  }

  sendFormSyncDetails(returnClobSettingsObj: any, ecSaveDtls: any[], message: string) {
    const apiRoot = this.generalConfig.api || this.generalConfig.base_url || '';
    this.fir163jTaggingService.syncForm(
      apiRoot,
      returnClobSettingsObj,
      ecSaveDtls,
      this.baseGlobalParams,
      this.filterParams
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (result: any) => {
        this.processing = false;
        this.loading = false;

        if (result && result.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar('Error syncing to Form 8990', 'gtw-snackbar--red');
        }
      },
      (error) => {
        this.processing = false;
        this.loading = false;
        this.gtwSnackbarService.openSnackBar('Error syncing to Form 8990', 'gtw-snackbar--red');
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
